import type { Entity } from '@backstage/catalog-model';
import {
  ANNOTATION_FRAMEWORK,
  ANNOTATION_GCP_PROJECT,
  ANNOTATION_LANGUAGE,
  ANNOTATION_PLATFORM,
  ANNOTATION_RUNTIME,
  ANNOTATION_TECHNOLOGY,
  getAnnotation,
} from './annotations';
import { getC4TypeLabel } from './c4Model';

const TAG_TECHNOLOGY: Record<string, string> = {
  angular: 'Angular',
  datastore: 'Datastore',
  elasticsearch: 'Elasticsearch',
  firebase: 'Firebase',
  firebird: 'Firebird',
  freemarker: 'FreeMarker',
  go: 'Go',
  java: 'Java',
  javascript: 'JavaScript',
  keycloak: 'Keycloak',
  kotlin: 'Kotlin',
  ktor: 'Ktor',
  memcached: 'Memcached',
  mongo: 'MongoDB',
  mongodb: 'MongoDB',
  mysql: 'MySQL',
  node: 'Node.js',
  oracle: 'Oracle',
  postgres: 'PostgreSQL',
  pubsub: 'Pub/Sub',
  python: 'Python',
  rabbitmq: 'RabbitMQ',
  react: 'React',
  redis: 'Redis',
  spring: 'Spring',
  sqlserver: 'SQL Server',
  typescript: 'TypeScript',
};

const MAX_TAGS = 2;

function fromTags(entity: Entity): string | undefined {
  const tags = entity?.metadata?.tags;
  if (!Array.isArray(tags)) {
    return undefined;
  }

  const matched = tags
    .filter((tag): tag is string => typeof tag === 'string')
    .map(tag => TAG_TECHNOLOGY[tag.trim().toLowerCase()])
    .filter((label): label is string => Boolean(label))

    .filter((label, index, all) => all.indexOf(label) === index)
    .slice(0, MAX_TAGS);

  return matched.length > 0 ? matched.join('/') : undefined;
}

export function getTechnology(entity: Entity): string | undefined {
  const technology = getAnnotation(entity, ANNOTATION_TECHNOLOGY);
  if (technology) {
    return technology;
  }

  const parts = [
    getAnnotation(entity, ANNOTATION_LANGUAGE),
    getAnnotation(entity, ANNOTATION_FRAMEWORK),
  ].filter((part): part is string => Boolean(part));

  if (parts.length > 0) {
    return parts.join('/');
  }

  const runtime = getAnnotation(entity, ANNOTATION_RUNTIME);
  if (runtime) {
    return runtime;
  }

  return fromTags(entity);
}

export function getPlatform(entity: Entity): string | undefined {
  return getAnnotation(entity, ANNOTATION_PLATFORM);
}

export function getGcpProject(entity: Entity): string | undefined {
  return getAnnotation(entity, ANNOTATION_GCP_PROJECT);
}

export function getDeploymentLine(entity: Entity): string | undefined {
  const parts = [getPlatform(entity), getGcpProject(entity)].filter(
    (part): part is string => Boolean(part),
  );
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export function getNodeTypeLine(entity: Entity): string {
  const label = getC4TypeLabel(entity);
  const technology = getTechnology(entity);

  return technology ? `[${label}: ${technology}]` : `[${label}]`;
}
